import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { LOGO_URL } from "@/data/content";
import { MS_SERVICES } from "@/data/managedServices";

const links = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/platform", label: "Platform" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [msOpen, setMsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header data-testid="main-nav" className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-900/10 bg-white/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="AIS logo" className="h-14 w-14 object-cover" />
          <div className="leading-tight">
            <span className="font-heading text-2xl tracking-tight text-zinc-900">AIS</span>
            <span className="ml-3 hidden font-mono text-sm uppercase tracking-[0.2em] text-zinc-600 lg:inline">Augmented Intelligence Systems</span>
          </div>
        </Link>
        <nav className="hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) => `font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${isActive ? "text-cyan-600" : "text-zinc-600 hover:text-zinc-900"}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="relative" onMouseEnter={() => setMsOpen(true)} onMouseLeave={() => setMsOpen(false)}>
            <NavLink to="/managed-services" data-testid="nav-link-managed-services"
              className={({ isActive }) => `flex items-center gap-1 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${isActive ? "text-cyan-600" : "text-zinc-600 hover:text-zinc-900"}`}>
              Managed AI Services <ChevronDown size={13} />
            </NavLink>
            {msOpen && (
              <div data-testid="nav-ms-dropdown" className="absolute left-1/2 top-full w-72 -translate-x-1/2 border border-zinc-900/10 bg-white/95 py-2 shadow-xl backdrop-blur-xl">
                {MS_SERVICES.map((s) => (
                  <Link key={s.slug} to={`/managed-services/${s.slug}`} data-testid={`nav-ms-${s.slug}`} onClick={() => setMsOpen(false)}
                    className="block px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 transition-colors hover:bg-cyan-500/5 hover:text-cyan-700">
                    {s.nav}
                  </Link>
                ))}
                <Link to="/managed-services" data-testid="nav-ms-view-all" onClick={() => setMsOpen(false)}
                  className="mt-1 block border-t border-zinc-900/10 px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-cyan-600 transition-colors hover:bg-cyan-500/5">
                  View All Managed Services →
                </Link>
              </div>
            )}
          </div>
          <NavLink to="/contact" data-testid="nav-link-contact"
            className={({ isActive }) => `font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${isActive ? "text-cyan-600" : "text-zinc-600 hover:text-zinc-900"}`}>
            Contact
          </NavLink>
          <button data-testid="nav-book-demo-btn" onClick={() => navigate("/contact")} className="btn-primary !px-5 !py-2.5 text-xs">
            Book Demo <ArrowUpRight size={14} />
          </button>
        </nav>
        <button data-testid="nav-mobile-toggle" className="text-zinc-900 md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div data-testid="nav-mobile-menu" className="max-h-[80vh] overflow-y-auto border-t border-zinc-900/10 bg-white/95 px-6 py-6 md:hidden">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-mono text-sm uppercase tracking-[0.2em] text-zinc-700">{l.label}</NavLink>
            ))}
            <NavLink to="/managed-services" onClick={() => setOpen(false)} className="font-mono text-sm uppercase tracking-[0.2em] text-cyan-700">Managed AI Services</NavLink>
            <div className="ml-3 flex flex-col gap-3 border-l border-zinc-900/10 pl-4">
              {MS_SERVICES.map((s) => (
                <NavLink key={s.slug} to={`/managed-services/${s.slug}`} onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">{s.nav}</NavLink>
              ))}
            </div>
            <NavLink to="/contact" onClick={() => setOpen(false)} className="font-mono text-sm uppercase tracking-[0.2em] text-zinc-700">Contact</NavLink>
            <button onClick={() => { setOpen(false); navigate("/contact"); }} className="btn-primary !py-3">Book Demo</button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;
